import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { API_ROUTES, buildAPIRoute } from "src/router/router";
import { WhatsAppInfo } from "src/types/private-schema/models/WhatsAppInfo";
import { WhatsAppProfile } from "src/types/private-schema/models/WhatsAppProfile";

export const useChannelProfile = (channelId: string) => {
  const queryClient = useQueryClient();
  const profileQueryKey = ["channel_profile", channelId];
  const infoQueryKey = ["channel_info", channelId];

  const fetchProfile = useCallback(async () => {
    const url = buildAPIRoute(API_ROUTES.CHANNEL_PROFILE_DETAIL, {
      ":id": channelId,
    });

    const { data } = await axios.get<WhatsAppProfile>(url, {
      headers: {
        "x-module": "channel",
      },
    });
    return data;
  }, [channelId]);

  const fetchInfo = useCallback(async () => {
    const url = buildAPIRoute(API_ROUTES.CHANNEL_INFO_DETAIL, {
      ":id": channelId,
    });

    const { data } = await axios.get<WhatsAppInfo>(url, {
      headers: {
        "x-module": "channel",
      },
    });
    return data;
  }, [channelId]);

  const {
    data: profileData,
    isLoading: isLoadingProfile,
    isError: isProfileError,
    error: profileError,
    refetch: refetchProfile,
  } = useQuery<WhatsAppProfile, Error>(profileQueryKey, fetchProfile, {
    enabled: !!channelId,
    retry: 1,
  });

  const {
    data: infoData,
    isLoading: isLoadingInfo,
    isError: isInfoError,
    error: infoError,
    refetch: refetchInfo,
  } = useQuery<WhatsAppInfo, Error>(infoQueryKey, fetchInfo, {
    enabled: !!channelId,
    retry: 1,
  });

  const {
    mutate: updateProfile,
    isLoading: isUpdating,
    isError: isUpdateError,
    error: updateError,
    isSuccess: isUpdateSuccess,
    reset: resetUpdateStatus,
  } = useMutation<WhatsAppProfile, Error, Partial<WhatsAppProfile>>(
    async (updatedProfile) => {
      const { data } = await axios.put<WhatsAppProfile>(
        buildAPIRoute(API_ROUTES.CHANNEL_PROFILE_DETAIL, {
          ":id": channelId,
        }),
        updatedProfile,
        {
          headers: {
            "x-module": "channel",
          },
        }
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(profileQueryKey);
      },
    }
  );

  const {
    mutate: registerPhone,
    isLoading: isRegistering,
    isError: isRegisterError,
    error: registerError,
  } = useMutation<void, Error>(
    async () => {
      const url = buildAPIRoute(API_ROUTES.CHANNEL_REGISTER, {
        ":id": channelId,
      });

      await axios.post(url, null, {
        headers: {
          "x-module": "channel",
        },
      });
    },
    {
      onSuccess: () => {
        refetchProfile();
        refetchInfo();
      },
    }
  );

  const isLoading = isLoadingProfile || isLoadingInfo;

  const refetch = useCallback(() => {
    refetchProfile();
    refetchInfo();
  }, [refetchProfile, refetchInfo]);

  return {
    profileData,
    infoData,
    isLoading,
    isLoadingProfile,
    isLoadingInfo,
    isProfileError,
    profileError,
    isInfoError,
    infoError,
    refetch,
    updateProfile,
    isUpdating,
    isUpdateError,
    updateError,
    isUpdateSuccess,
    resetUpdateStatus,
    registerPhone,
    isRegistering,
    isRegisterError,
    registerError,
  };
};

export default useChannelProfile;
